import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ErrorApi } from '../lib/api'
import type { Liquidacion, Usuario } from '../lib/tipos'

export function Liquidaciones({ usuario }: { usuario: Usuario }) {
  const clienteQuery = useQueryClient()
  const [mes, setMes] = useState(() => new Date().toISOString().slice(0, 7))
  const [aviso, setAviso] = useState<string | null>(null)
  const puedeCerrar = ['supervisor', 'admin'].includes(usuario.rol)

  const consulta = useQuery({
    queryKey: ['liquidaciones', mes],
    queryFn: () => api<Liquidacion>(`/api/liquidaciones?mes=${mes}`),
  })

  const cerrar = useMutation({
    mutationFn: () => api<Liquidacion>('/api/liquidaciones/cerrar', { metodo: 'POST', cuerpo: { mes } }),
    onSuccess: (datos) => {
      setAviso(null)
      clienteQuery.setQueryData(['liquidaciones', mes], datos)
    },
    onError: (e) => setAviso(e instanceof ErrorApi ? e.message : 'No se ha podido cerrar'),
  })

  const corte = useMutation({
    mutationFn: (dia: number) => api('/api/liquidaciones/corte', { metodo: 'POST', cuerpo: { dia } }),
    onSuccess: () => void clienteQuery.invalidateQueries({ queryKey: ['liquidaciones'] }),
  })

  const datos = consulta.data

  return (
    <div>
      <h1 className="mb-1 text-lg font-bold">Liquidaciones</h1>
      <p className="mb-4 text-sm text-slate-500">
        Al cerrar un mes se congelan los puntos de cada comercial. A partir de ahí, la baja de
        un contrato de ese mes ya no se resta de él: se descuenta del mes en curso.
      </p>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <input
          type="month"
          value={mes}
          onChange={(e) => setMes(e.target.value)}
          className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-950"
        />
        {datos?.cerrada ? (
          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold dark:bg-slate-800">
            Cerrada
          </span>
        ) : (
          <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800">
            Abierta
          </span>
        )}
        {puedeCerrar && datos && !datos.cerrada && (
          <button
            onClick={() => cerrar.mutate()}
            disabled={!datos.se_puede_cerrar || cerrar.isPending}
            title={datos.se_puede_cerrar ? '' : `El corte es el día ${datos.dia_corte} del mes siguiente`}
            className="rounded-lg bg-marca-700 px-4 py-2 text-sm font-bold text-white transition hover:bg-marca-800 disabled:opacity-40"
          >
            Cerrar el mes
          </button>
        )}
      </div>

      {aviso && (
        <p className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{aviso}</p>
      )}

      <div className="mb-4 overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-400 dark:bg-slate-800">
            <tr>
              <th className="px-4 py-2">Comercial</th>
              <th className="px-4 py-2 text-right">Movimientos</th>
              <th className="px-4 py-2 text-right">Puntos</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {datos?.comerciales.length === 0 && (
              <tr>
                <td colSpan={3} className="px-4 py-6 text-center text-slate-400">
                  Ese mes no tiene movimientos.
                </td>
              </tr>
            )}
            {datos?.comerciales.map((fila) => (
              <tr key={fila.usuario_id}>
                <td className="px-4 py-2">{fila.nombre}</td>
                <td className="px-4 py-2 text-right tabular-nums text-slate-500">{fila.movimientos}</td>
                <td className="px-4 py-2 text-right font-semibold tabular-nums">
                  {fila.puntos.toLocaleString('es-ES')}
                </td>
              </tr>
            ))}
          </tbody>
          {datos && datos.comerciales.length > 0 && (
            <tfoot className="border-t border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800">
              <tr>
                <td className="px-4 py-2 text-xs font-bold uppercase tracking-wide text-slate-400">Total</td>
                <td />
                <td className="px-4 py-2 text-right font-bold tabular-nums">
                  {datos.total.toLocaleString('es-ES')}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {puedeCerrar && datos && (
        <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm dark:border-slate-800 dark:bg-slate-900">
          <h2 className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-400">Día de corte</h2>
          <p className="mb-3 text-xs text-slate-500">
            Día del mes siguiente en que se cierra el mes anterior. La liquidación de septiembre
            se cierra el {datos.dia_corte} de octubre.
          </p>
          <div className="flex gap-2">
            <input
              type="number"
              min="1"
              max="28"
              defaultValue={datos.dia_corte}
              onBlur={(e) => corte.mutate(Number(e.target.value))}
              className="w-20 rounded-lg border border-slate-200 px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-950"
            />
          </div>
        </div>
      )}
    </div>
  )
}
